/**
 * Admin dashboard — restyled to the EvoCha operations design: gradient KPI
 * strip, four performance panels, chart row and three operational tables.
 * Every figure comes from GET /admin/v1/dashboard; deltas compare the selected
 * window with the preceding window of the same length.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, shortDate, shortDateTime, tzs } from "../lib/admin";
import type { AdminOrder, AuditEvent } from "../lib/types";
import { GtvBars, Donut, Sparkline, type BarPoint, type DonutSlice } from "../components/Charts";
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Banknote,
  BarChart3,
  CalendarDays,
  Layers,
  Link2,
  MapPin,
  Minus,
  PieChart,
  Settings,
  ShieldCheck,
  Table2,
  Trophy,
  Users,
  Wallet,
  X,
} from "lucide-react";

interface DashboardData {
  period: { kind: string; from: string; to: string; days: number };
  previous: {
    from: string;
    to: string;
    gross_minor: number;
    commission_minor: number;
    orders: number;
    success: number;
    failed: number;
    pending: number;
    review: number;
    success_rate: number;
    take_rate: number;
    average_order_minor: number;
    new_agents: number;
  };
  kpis: {
    gross_minor: number;
    commission_minor: number;
    orders: number;
    success: number;
    failed: number;
    pending: number;
    review: number;
    success_rate: number;
    take_rate: number;
    average_order_minor: number;
    unknown_exposure_minor: number;
    agents: number;
    active_agents: number;
    new_agents: number;
  };
  daily: Array<{
    date: string;
    orders: number;
    success: number;
    failed: number;
    pending: number;
    review: number;
    agents: number;
    gross_minor: number;
    commission_minor: number;
  }>;
  hourly: Array<{ hour: number; orders: number; gross_minor: number; commission_minor: number }>;
  network_mix: Array<{ mno: string; count: number; gross_minor: number; pct: number }>;
  amount_mix: Array<{ label: string; count: number; gross_minor: number }>;
  regions: Array<{ region: string; orders: number; gross_minor: number; pct: number }>;
  network_health: Array<{
    mno: string;
    orders: number;
    success: number;
    failed: number;
    gross_minor: number;
    success_rate: number;
  }>;
  top_agents: Array<{
    agent_id: string;
    agent_code: string;
    business_name: string;
    region: string;
    orders: number;
    gross_minor: number;
    commission_minor: number;
    pct: number;
  }>;
  kyc: { pending: number; approved: number; rejected: number };
  recent_orders: AdminOrder[];
  unknown_orders: AdminOrder[];
  activity: AuditEvent[];
}

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7D" },
  { value: "30d", label: "30D" },
] as const;

type PeriodValue = (typeof PERIODS)[number]["value"];

const NETWORK_COLORS: Record<string, string> = {
  vodacom: "#2563eb",
  tigo: "#22b8cf",
  yas: "#22b8cf",
  airtel: "#ef4444",
  halotel: "#f59e0b",
  ttcl: "#eab308",
  zantel: "#8b5cf6",
};

const FALLBACK_COLORS = ["#6366f1", "#0ea5e9", "#14b8a6", "#f97316", "#a855f7"];
const SIZE_COLORS = ["#2563eb", "#22c55e", "#f59e0b", "#ef4444", "#94a3b8"];

// ── Formatting ────────────────────────────────────────────────
function tsh(minor: number): string {
  return tzs(minor).replace(/^TZS/, "TSh");
}

function tshCompact(minor: number): string {
  const major = minor / 100;
  const abs = Math.abs(major);
  if (abs >= 1_000_000_000) return `TSh ${(major / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `TSh ${(major / 1_000_000).toFixed(1)}M`;
  if (abs >= 100_000) return `TSh ${Math.round(major / 1_000)}K`;
  return tsh(minor);
}

function num(value: number): string {
  return Math.round(value).toLocaleString("en-GB");
}

function round1(value: number): string {
  return value.toFixed(1);
}

function share(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0;
}

function longDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function updatedLabel(ms: number): string {
  return new Date(ms)
    .toLocaleString("en-GB", {
      timeZone: "Africa/Dar_es_Salaam",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "");
}

function timeOnly(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString("en-GB", {
    timeZone: "Africa/Dar_es_Salaam",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// ── Deltas ────────────────────────────────────────────────────
type TrendDir = "up" | "down" | "flat" | "new" | "none";
interface Trend {
  dir: TrendDir;
  text: string;
}

function trendValue(value: number, suffix: "%" | " pp"): Trend {
  const rounded = Math.round(value * 10) / 10;
  if (rounded === 0) return { dir: "flat", text: `0.0${suffix}` };
  return { dir: rounded > 0 ? "up" : "down", text: `${Math.abs(rounded).toFixed(1)}${suffix}` };
}

function trendPercent(current: number, previous: number): Trend {
  if (previous === 0) {
    return current === 0 ? { dir: "none", text: "—" } : { dir: "new", text: "new" };
  }
  return trendValue(((current - previous) / previous) * 100, "%");
}

function trendPoints(current: number, previous: number): Trend {
  return trendValue(current - previous, " pp");
}

function Delta({ trend }: { trend: Trend }) {
  const Icon =
    trend.dir === "up" ? ArrowUp : trend.dir === "down" ? ArrowDown : trend.dir === "new" ? ArrowUp : Minus;
  return (
    <span className={`dash-delta dash-delta-${trend.dir}`}>
      <Icon size={11} strokeWidth={2.5} />
      {trend.text}
    </span>
  );
}

// ── Building blocks ───────────────────────────────────────────
interface Metric {
  label: string;
  value: string;
  trend?: Trend;
}

function PerformancePanel({
  title,
  icon,
  href,
  metrics,
}: {
  title: string;
  icon: React.ReactNode;
  href: string;
  metrics: Metric[];
}) {
  return (
    <section className="dash-panel">
      <header className="dash-panel-head">
        <span className="dash-panel-icon">{icon}</span>
        <h3>{title}</h3>
        <Link to={href} className="dash-link">
          Details <span aria-hidden="true">→</span>
        </Link>
      </header>
      <div className="dash-panel-metrics">
        {metrics.map((metric) => (
          <div className="dash-metric" key={metric.label}>
            <span className="dash-metric-label">{metric.label}</span>
            <strong className="dash-metric-value">{metric.value}</strong>
            <span className="dash-metric-delta">{metric.trend && <Delta trend={metric.trend} />}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function KpiCard({
  tone,
  label,
  value,
  delta,
  note,
  spark,
  onDrill,
}: {
  tone: "navy" | "green" | "orange" | "violet" | "teal";
  label: string;
  value: string;
  delta: React.ReactNode;
  note?: string;
  spark: number[];
  onDrill?: () => void;
}) {
  const body = (
    <>
      <span className="dash-kpi-label">{label}</span>
      {onDrill && (
        <span className="dash-kpi-drill" aria-hidden="true">
          <Table2 size={13} />
          rows
        </span>
      )}
      <div className="dash-kpi-body">
        <div className="dash-kpi-main">
          <strong className="dash-kpi-value">{value}</strong>
          <div className="dash-kpi-meta">
            {delta}
            {note && <span className="dash-kpi-note">{note}</span>}
          </div>
        </div>
        <div className="dash-kpi-spark">
          <Sparkline values={spark} color="rgba(255,255,255,0.85)" />
        </div>
      </div>
    </>
  );

  if (!onDrill) return <div className={`dash-kpi dash-kpi-${tone}`}>{body}</div>;
  return (
    <button
      type="button"
      className={`dash-kpi dash-kpi-${tone} dash-kpi-clickable`}
      onClick={onDrill}
      title={`Show the rows that add up to ${label}`}
    >
      {body}
    </button>
  );
}

type DrillMetric = "gtv" | "active_agents" | "transactions" | "take_rate" | "success_rate";

interface DrillRow {
  ref?: string;
  agent_id?: string;
  agent?: { code?: string; business_name?: string } | null;
  status?: string;
  status_label?: string;
  amount_minor?: number;
  amount_display?: string;
  commission_minor?: number;
  mno?: string;
  channel?: string;
  failure_reason?: string | null;
  created_at?: string | null;
  code?: string;
  business_name?: string;
  region?: string;
  kyc_status?: string;
  window_orders?: number;
  window_gross_minor?: number;
  window_gross_display?: string;
}

interface DrillData {
  metric: DrillMetric;
  row_kind: "order" | "agent";
  period: { kind: string; from: string; to: string; days: number };
  headline: { label: string; value: string; note?: string };
  totals: Record<string, number>;
  rows: DrillRow[];
  shown_rows: number;
  total_rows: number;
  hidden_rows: number;
  hidden_amount_minor: number;
  truncated: boolean;
  sql: string;
}

const DRILL_TITLES: Record<DrillMetric, string> = {
  gtv: "Total GTV",
  active_agents: "Active agents",
  transactions: "Transactions",
  take_rate: "EvoCha net take rate",
  success_rate: "Success rate",
};

/** Third summary cell — the wording changes so the sum reads as a reconciliation. */
const DRILL_SUM_LABELS: Record<DrillMetric, string> = {
  gtv: "Rows sum to",
  take_rate: "Gross of these rows",
  transactions: "Value of these rows",
  success_rate: "Value not delivered",
  active_agents: "Gross in window",
};

function DrillDrawer({
  metric,
  period,
  onClose,
}: {
  metric: DrillMetric;
  period: PeriodValue;
  onClose: () => void;
}) {
  const { data, isLoading, error, refetch } = useAdminQuery<DrillData>(
    ["dashboard-drill", metric, period],
    "/admin/v1/dashboard/drill",
    { metric, period },
  );
  const isAgents = data?.row_kind === "agent";
  const shownAmount = (data?.rows ?? []).reduce(
    (sum, row) => sum + (isAgents ? (row.window_gross_minor ?? 0) : (row.amount_minor ?? 0)),
    0,
  );

  return (
    <div className="admin-drawer-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label={`${DRILL_TITLES[metric]} details`}>
      <div className="admin-drawer dash-drill" onClick={(e) => e.stopPropagation()}>
        <div className="dash-drill-head">
          <div>
            <h3>{DRILL_TITLES[metric]}</h3>
            <span className="ev-body-micro muted">
              {data ? `${longDate(data.period.from)} – ${longDate(data.period.to)} · EAT` : "Loading window…"}
            </span>
          </div>
          <button className="ev-icon-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {isLoading && <LoadingCard label="Loading rows…" />}
        {error && <ErrorCard error={error} onRetry={() => void refetch()} />}

        {data && (
          <>
            <div className="dash-drill-summary">
              <div>
                <span className="ev-body-micro muted">{data.headline.label}</span>
                <strong>{data.headline.value}</strong>
              </div>
              <div>
                <span className="ev-body-micro muted">Rows</span>
                <strong>{num(data.total_rows)}</strong>
              </div>
              <div>
                <span className="ev-body-micro muted">{DRILL_SUM_LABELS[data.metric]}</span>
                <strong>{tshCompact(shownAmount)}</strong>
              </div>
            </div>
            {data.headline.note && <p className="dash-drill-note">{data.headline.note}</p>}

            <div className="dash-table-wrap dash-drill-table">
              <table className="dash-table">
                <thead>
                  <tr>
                    {isAgents ? (
                      <>
                        <th>Agent</th>
                        <th>Region</th>
                        <th>KYC</th>
                        <th className="num">Sales</th>
                        <th className="num">GTV (TSh)</th>
                      </>
                    ) : (
                      <>
                        <th>When</th>
                        <th>Agent</th>
                        <th>Net</th>
                        <th className="num">Amount</th>
                        <th className="num">Commission</th>
                        <th>Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, index) =>
                    isAgents ? (
                      <tr key={row.agent_id ?? index}>
                        <td>
                          <span className="dash-cell-title">{row.business_name || "Unnamed agent"}</span>
                          <span className="dash-cell-sub">{row.code}</span>
                        </td>
                        <td>{row.region}</td>
                        <td>{row.kyc_status}</td>
                        <td className="num">{num(row.window_orders ?? 0)}</td>
                        <td className="num">{num((row.window_gross_minor ?? 0) / 100)}</td>
                      </tr>
                    ) : (
                      <tr key={row.ref ?? index}>
                        <td className="num">{shortDateTime(row.created_at)}</td>
                        <td>
                          <span className="dash-cell-title">{row.agent?.business_name || "Unknown agent"}</span>
                          <span className="dash-cell-sub">{row.agent?.code}</span>
                        </td>
                        <td>{(row.mno || "—").toUpperCase()}</td>
                        <td className="num">{num((row.amount_minor ?? 0) / 100)}</td>
                        <td className="num">{num((row.commission_minor ?? 0) / 100)}</td>
                        <td>
                          <span className={`dash-pill dash-pill-${orderStatusClass(row.status as AdminOrder["status"])}`}>
                            {row.status === "MANUAL_REVIEW" ? "Review" : (row.status_label ?? row.status)}
                          </span>
                          {row.failure_reason && <span className="dash-cell-sub">{row.failure_reason}</span>}
                        </td>
                      </tr>
                    ),
                  )}
                  {data.rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="dash-table-empty">
                        No rows contribute to this figure in this period.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {data.truncated ? (
              <p className="dash-drill-note">
                Showing the top {num(data.shown_rows)} of {num(data.total_rows)} rows. The remaining{" "}
                {num(data.hidden_rows)} rows add {tshCompact(data.hidden_amount_minor)}, so shown plus hidden equals
                the card figure.
              </p>
            ) : (
              data.total_rows > 0 && (
                <p className="dash-drill-note">
                  All {num(data.total_rows)} contributing rows are listed — nothing is hidden behind this figure.
                </p>
              )
            )}

            {data.sql && (
              <details className="dash-drill-sql">
                <summary>SQL behind this figure</summary>
                <pre>{data.sql}</pre>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ChartHead({ icon, title, aside }: { icon: React.ReactNode; title: string; aside?: React.ReactNode }) {
  return (
    <div className="dash-card-head">
      <div className="dash-card-title">
        <span className="dash-card-icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      {aside ? <span className="dash-card-aside">{aside}</span> : null}
    </div>
  );
}

function shortBand(label: string): string {
  const numbers = label.match(/[\d,]+/g) ?? [];
  const compact = (value: string) => {
    const amount = Number(value.replaceAll(",", ""));
    return amount >= 1000 ? `${Math.round(amount / 1000)}k` : value;
  };
  if (label.trim().startsWith("<")) return `< ${compact(numbers[0] ?? "0")}`;
  if (label.trim().endsWith("+")) return `${compact(numbers[0] ?? "0")}+`;
  return numbers.length >= 2 ? `${compact(numbers[0] ?? "0")}–${compact(numbers[1] ?? "0")}` : label;
}

function actionLabel(action: string): string {
  return action.replace(/^admin\./, "").replaceAll("_", " ").replaceAll(".", " · ");
}

function orderStatusClass(status: AdminOrder["status"]): string {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "PENDING":
      return "pending";
    case "MANUAL_REVIEW":
      return "review";
    case "FAILED":
      return "failed";
    default:
      return "neutral";
  }
}

function providerState(rate: number): { label: string; tone: string } {
  if (rate >= 95) return { label: "Online", tone: "online" };
  if (rate >= 80) return { label: "Degraded", tone: "degraded" };
  return { label: "Offline", tone: "offline" };
}

export function Dashboard() {
  const [period, setPeriod] = useState<PeriodValue>("today");
  const [drill, setDrill] = useState<DrillMetric | null>(null);
  const { data, isLoading, error, refetch, dataUpdatedAt } = useAdminQuery<DashboardData>(
    ["dashboard", period],
    "/admin/v1/dashboard",
    { period },
  );

  const isToday = period === "today";
  const periodLabel = PERIODS.find((item) => item.value === period)?.label ?? "Today";
  const lastUpdated = dataUpdatedAt ? updatedLabel(dataUpdatedAt) : "—";

  useEffect(() => {
    if (!drill) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrill(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill]);

  const kpis = data?.kpis;
  const prev = data?.previous;
  const kyc = data?.kyc ?? { pending: 0, approved: 0, rejected: 0 };
  const healthy = !kpis || kpis.orders === 0 || kpis.success_rate >= 90;

  const hourly = data?.hourly ?? [];
  const daily = data?.daily ?? [];
  const peakHour = hourly.reduce(
    (best, entry) => (entry.orders > best.orders ? entry : best),
    { hour: 0, orders: 0, gross_minor: 0, commission_minor: 0 },
  );

  const chartPoints: BarPoint[] = isToday
    ? hourly.map((entry) => ({ label: `${String(entry.hour).padStart(2, "0")}:00`, value: entry.gross_minor }))
    : daily.map((entry) => ({ label: shortDate(entry.date), value: entry.gross_minor }));

  const grossSpark = isToday ? hourly.map((entry) => entry.gross_minor) : daily.map((entry) => entry.gross_minor);
  const orderSpark = isToday ? hourly.map((entry) => entry.orders) : daily.map((entry) => entry.orders);
  const agentSpark = daily.map((entry) => entry.agents);
  const takeSpark = daily.map((entry) => share(entry.commission_minor, entry.gross_minor));
  const successSpark = daily.map((entry) => share(entry.success, entry.orders));

  const successTotal = Math.max(kpis?.success ?? 0, 1);
  const networkSlices: DonutSlice[] = (data?.network_mix ?? []).map((entry, index) => ({
    label: entry.mno.toUpperCase(),
    value: entry.count,
    color: NETWORK_COLORS[entry.mno.toLowerCase()] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length] ?? "#94a3b8",
    display: `${Math.round(share(entry.count, successTotal))}%`,
  }));
  const sizeSlices: DonutSlice[] = (data?.amount_mix ?? []).map((band, index) => ({
    label: shortBand(band.label),
    value: band.count,
    color: SIZE_COLORS[index] ?? "#94a3b8",
    display: `${Math.round(share(band.count, successTotal))}%`,
  }));

  const regions = data?.regions ?? [];
  const regionMax = Math.max(1, ...regions.map((entry) => entry.gross_minor));
  const topAgents = data?.top_agents ?? [];
  const networkHealth = data?.network_health ?? [];

  const days = Math.max(data?.period.days ?? 1, 1);
  const activeAgents = kpis?.active_agents ?? 0;
  const registeredAgents = kpis?.agents ?? 0;
  const gtvPerAgentDay = activeAgents > 0 ? (kpis?.gross_minor ?? 0) / activeAgents / days : 0;
  const txnPerAgentDay = activeAgents > 0 ? (kpis?.orders ?? 0) / activeAgents / days : 0;
  const contribution = kpis?.commission_minor ?? 0;
  const contributionPerAgent = activeAgents > 0 ? contribution / activeAgents : 0;
  const contributionPerTxn = (kpis?.success ?? 0) > 0 ? contribution / (kpis?.success ?? 1) : 0;
  const prevContributionPerTxn =
    (prev?.success ?? 0) > 0 ? (prev?.commission_minor ?? 0) / (prev?.success ?? 1) : 0;
  const kycDecided = kyc.approved + kyc.rejected;

  const commercialMetrics: Metric[] = kpis && prev
    ? [
        { label: "Registered agents", value: num(registeredAgents) },
        { label: "Active agents", value: num(activeAgents) },
        { label: "Activation %", value: `${round1(share(activeAgents, registeredAgents))}%` },
        {
          label: "GTV",
          value: tshCompact(kpis.gross_minor),
          trend: trendPercent(kpis.gross_minor, prev.gross_minor),
        },
        { label: "GTV / agent / day", value: tshCompact(gtvPerAgentDay) },
        { label: "Txns / agent / day", value: txnPerAgentDay.toFixed(1) },
        {
          label: "Avg order value",
          value: tsh(kpis.average_order_minor),
          trend: trendPercent(kpis.average_order_minor, prev.average_order_minor),
        },
        {
          label: "New agents",
          value: num(kpis.new_agents),
          trend: trendPercent(kpis.new_agents, prev.new_agents),
        },
      ]
    : [];

  const financialMetrics: Metric[] = kpis && prev
    ? [
        {
          label: "Gross contribution",
          value: tshCompact(contribution),
          trend: trendPercent(contribution, prev.commission_minor),
        },
        { label: "Contribution / agent", value: tsh(contributionPerAgent) },
        {
          label: "Contribution / txn",
          value: tsh(contributionPerTxn),
          trend: trendPercent(contributionPerTxn, prevContributionPerTxn),
        },
        {
          label: "Net take rate",
          value: `${round1(kpis.take_rate)}%`,
          trend: trendPoints(kpis.take_rate, prev.take_rate),
        },
        {
          label: "Avg order value",
          value: tsh(kpis.average_order_minor),
          trend: trendPercent(kpis.average_order_minor, prev.average_order_minor),
        },
        { label: "Unknown exposure", value: tshCompact(kpis.unknown_exposure_minor) },
      ]
    : [];

  const operationalMetrics: Metric[] = kpis && prev
    ? [
        {
          label: "Success rate",
          value: `${round1(kpis.success_rate)}%`,
          trend: trendPoints(kpis.success_rate, prev.success_rate),
        },
        {
          label: "Transactions",
          value: num(kpis.orders),
          trend: trendPercent(kpis.orders, prev.orders),
        },
        {
          label: "Successful",
          value: num(kpis.success),
          trend: trendPercent(kpis.success, prev.success),
        },
        { label: "Failed", value: num(kpis.failed), trend: trendPercent(kpis.failed, prev.failed) },
        { label: "Pending", value: num(kpis.pending), trend: trendPercent(kpis.pending, prev.pending) },
        {
          label: "Manual review",
          value: num(kpis.review),
          trend: trendPercent(kpis.review, prev.review),
        },
        { label: "Peak hour (EAT)", value: peakHour.orders > 0 ? `${String(peakHour.hour).padStart(2, "0")}:00` : "—" },
      ]
    : [];

  const riskMetrics: Metric[] = kpis
    ? [
        { label: "KYC pending", value: num(kyc.pending) },
        { label: "KYC approved", value: num(kyc.approved) },
        { label: "KYC rejected", value: num(kyc.rejected) },
        { label: "KYC approval rate", value: kycDecided > 0 ? `${round1(share(kyc.approved, kycDecided))}%` : "—" },
        { label: "In manual review", value: num(kpis.review) },
        { label: "Unknown exposure", value: tshCompact(kpis.unknown_exposure_minor) },
        {
          label: "Failure rate",
          value: `${round1(100 - kpis.success_rate)}%`,
          trend: prev ? trendPoints(100 - kpis.success_rate, 100 - prev.success_rate) : undefined,
        },
      ]
    : [];

  return (
    <div className="dash-page">
      <header className="dash-head">
        <div className="dash-head-title">
          <h1>EvoCha Dashboard</h1>
          <p>Airtime today. A bigger tomorrow.</p>
        </div>

        <div className="dash-head-controls">
          <span className="dash-date-pill">
            <CalendarDays size={14} />
            {data ? `${longDate(data.period.from)} – ${longDate(data.period.to)}` : "Loading period…"}
          </span>

          <div className="dash-seg" role="group" aria-label="Dashboard period">
            {PERIODS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={period === item.value ? "dash-seg-btn dash-seg-btn-active" : "dash-seg-btn"}
                aria-pressed={period === item.value}
                onClick={() => setPeriod(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="dash-status">
            <span className={healthy ? "dash-status-dot" : "dash-status-dot dash-status-dot-warn"} />
            <div className="dash-status-text">
              <strong>{healthy ? "All Systems Operational" : "Degraded performance"}</strong>
              <span>Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </header>

      {isLoading && <LoadingCard label="Loading dashboard…" />}
      {error && <ErrorCard error={error} onRetry={() => void refetch()} />}

      {data && kpis && prev && (
        <>
          <div className="dash-kpi-row">
            <KpiCard
              tone="navy"
              label={`Total GTV (${periodLabel})`}
              value={tshCompact(kpis.gross_minor)}
              delta={<Delta trend={trendPercent(kpis.gross_minor, prev.gross_minor)} />}
              note={`${num(kpis.success)} sales`}
              spark={grossSpark}
              onDrill={() => setDrill("gtv")}
            />
            <KpiCard
              tone="green"
              label="Active Agents"
              value={num(activeAgents)}
              delta={
                <span className={`dash-delta ${kpis.new_agents > 0 ? "dash-delta-up" : "dash-delta-flat"}`}>
                  <ArrowUp size={11} strokeWidth={2.5} />
                  {num(kpis.new_agents)} new
                </span>
              }
              note={`of ${num(registeredAgents)}`}
              spark={agentSpark}
              onDrill={() => setDrill("active_agents")}
            />
            <KpiCard
              tone="orange"
              label={`Transactions (${periodLabel})`}
              value={num(kpis.orders)}
              delta={<Delta trend={trendPercent(kpis.orders, prev.orders)} />}
              note={`${num(kpis.pending)} in flight`}
              spark={orderSpark}
              onDrill={() => setDrill("transactions")}
            />
            <KpiCard
              tone="violet"
              label="EvoCha Net Take Rate"
              value={`${round1(kpis.take_rate)}%`}
              delta={<Delta trend={trendPoints(kpis.take_rate, prev.take_rate)} />}
              note={`of ${tshCompact(kpis.gross_minor)}`}
              spark={takeSpark}
              onDrill={() => setDrill("take_rate")}
            />
            <KpiCard
              tone="teal"
              label="Success Rate"
              value={`${round1(kpis.success_rate)}%`}
              delta={<Delta trend={trendPoints(kpis.success_rate, prev.success_rate)} />}
              note={`${num(kpis.failed)} failed`}
              spark={successSpark}
              onDrill={() => setDrill("success_rate")}
            />
          </div>

          <div className="dash-panels">
            <PerformancePanel title="Commercial Performance" icon={<Users size={16} />} href="/agents" metrics={commercialMetrics} />
            <PerformancePanel title="Financial Performance" icon={<Banknote size={16} />} href="/finance" metrics={financialMetrics} />
            <PerformancePanel title="Operational Performance" icon={<Settings size={16} />} href="/orders" metrics={operationalMetrics} />
            <PerformancePanel title="Risk &amp; Compliance" icon={<ShieldCheck size={16} />} href="/kyc" metrics={riskMetrics} />
          </div>

          <div className="dash-charts">
            <section className="dash-card dash-chart-trend">
              <ChartHead
                icon={<BarChart3 size={16} />}
                title="GTV Trend"
                aside={isToday ? "TSh per hour" : "TSh per day"}
              />
              <div className="dash-chart-body">
                <GtvBars
                  points={chartPoints}
                  averageWindow={isToday ? 4 : 7}
                  averageLabel={isToday ? "4-hour average" : "7-day average"}
                />
                <div className="chart-legend dash-chart-legend">
                  <span className="chart-legend-item">
                    <i className="swatch-bar" /> {isToday ? "Hourly GTV" : "Daily GTV"}
                  </span>
                  <span className="chart-legend-item">
                    <i className="swatch-line" /> {isToday ? "4-hour average" : "7-day average"}
                  </span>
                </div>
              </div>
            </section>

            <section className="dash-card">
              <ChartHead icon={<PieChart size={16} />} title="Transactions by Network" />
              <div className="dash-chart-body">
                <Donut slices={networkSlices} centerTop={num(kpis.success)} centerBottom="transactions" />
                {networkSlices.length === 0 && <span className="dash-empty-note">No successful sales yet.</span>}
              </div>
            </section>

            <section className="dash-card">
              <ChartHead icon={<Layers size={16} />} title="Transactions by Size" />
              <div className="dash-chart-body">
                <Donut slices={sizeSlices} centerTop={num(kpis.success)} centerBottom="transactions" />
                {kpis.success === 0 && <span className="dash-empty-note">No successful sales yet.</span>}
              </div>
            </section>

            <section className="dash-card">
              <ChartHead icon={<MapPin size={16} />} title="Top Performing Regions" aside="GTV" />
              <div className="dash-chart-body dash-chart-body-top">
                {regions.length === 0 && <span className="dash-empty-note">No regional sales yet.</span>}
                <div className="dash-region-list">
                  {regions.map((region) => (
                    <div className="dash-region-row" key={region.region}>
                      <span className="dash-region-name" title={region.region}>
                        {region.region}
                      </span>
                      <span className="dash-region-track">
                        <span style={{ width: `${Math.max(share(region.gross_minor, regionMax), 4)}%` }} />
                      </span>
                      <strong className="dash-region-value">{tshCompact(region.gross_minor)}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>

          <div className="dash-tables">
            <section className="dash-card dash-table-card">
              <ChartHead
                icon={<Trophy size={16} style={{ color: "#f59e0b" }} />}
                title="Top Agents by GTV"
                aside={
                  <Link to="/agents" className="dash-link">
                    View all <span aria-hidden="true">→</span>
                  </Link>
                }
              />
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Agent name / ID</th>
                      <th>Region</th>
                      <th className="num">Transactions</th>
                      <th className="num">GTV (TSh)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topAgents.map((agent, index) => (
                      <tr key={agent.agent_id}>
                        <td className="dash-rank-cell">{index + 1}</td>
                        <td>
                          <span className="dash-cell-title">{agent.business_name || "Unnamed agent"}</span>
                          <span className="dash-cell-sub">{agent.agent_code || agent.agent_id.slice(0, 8)}</span>
                        </td>
                        <td>{agent.region}</td>
                        <td className="num">{num(agent.orders)}</td>
                        <td className="num">{num(agent.gross_minor / 100)}</td>
                      </tr>
                    ))}
                    {topAgents.length === 0 && (
                      <tr>
                        <td colSpan={5} className="dash-table-empty">
                          No successful sales in this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="dash-card dash-table-card">
              <ChartHead
                icon={<Wallet size={16} style={{ color: "#10b981" }} />}
                title="Recent Transactions"
                aside={
                  <Link to="/orders" className="dash-link">
                    View all <span aria-hidden="true">→</span>
                  </Link>
                }
              />
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Agent</th>
                      <th>Network</th>
                      <th className="num">Amount (TSh)</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.recent_orders ?? []).slice(0, 6).map((order) => (
                      <tr key={order.ref}>
                        <td className="dash-mono">{timeOnly(order.created_at)}</td>
                        <td>
                          <span className="dash-cell-title">
                            {order.agent?.business_name ?? order.agent_id.slice(0, 8)}
                          </span>
                        </td>
                        <td>{order.mno.toUpperCase()}</td>
                        <td className="num">{num(order.amount_minor / 100)}</td>
                        <td>
                          <span className={`dash-pill dash-pill-${orderStatusClass(order.status)}`}>
                            {order.status === "MANUAL_REVIEW" ? "Review" : order.status_label}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {(data.recent_orders ?? []).length === 0 && (
                      <tr>
                        <td colSpan={5} className="dash-table-empty">
                          No orders recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="dash-card dash-table-card">
              <ChartHead
                icon={<Link2 size={16} />}
                title="Provider Status"
                aside={data ? `${networkHealth.length} live networks` : "—"}
              />
              <div className="dash-table-wrap">
                <table className="dash-table">
                  <thead>
                    <tr>
                      <th>Provider</th>
                      <th>Status</th>
                      <th className="num">Success</th>
                      <th className="num">Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {networkHealth.map((entry) => {
                      const state = providerState(entry.success_rate);
                      return (
                        <tr key={entry.mno}>
                          <td>{entry.mno.toUpperCase()}</td>
                          <td>
                            <span className={`dash-provider-state dash-provider-${state.tone}`}>
                              <i />
                              {state.label}
                            </span>
                          </td>
                          <td className="num">{entry.success_rate.toFixed(1)}%</td>
                          <td className="num">{num(entry.orders)}</td>
                        </tr>
                      );
                    })}
                    {networkHealth.length === 0 && (
                      <tr>
                        <td colSpan={4} className="dash-table-empty">
                          No provider activity yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <section className="dash-card dash-activity-card">
            <ChartHead
              icon={<Activity size={16} />}
              title="Recent Activity"
              aside={`Latest ${Math.min(data.activity.length, 8)} audit events`}
            />
            {(data.activity ?? []).length === 0 ? (
              <span className="dash-empty-note">No audit events yet.</span>
            ) : (
              <div className="dash-activity-list">
                {data.activity.slice(0, 8).map((event) => (
                  <div className="dash-activity-row" key={event.id}>
                    <span className="dash-activity-dot" />
                    <span className="dash-activity-text">
                      <strong>{event.actor_type ?? "system"}</strong> {actionLabel(event.action)}{" "}
                      <span className="dash-mono">{event.object_id?.slice(0, 12) ?? ""}</span>
                    </span>
                    <span className="dash-activity-time">{shortDateTime(event.occurred_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {drill && <DrillDrawer metric={drill} period={period} onClose={() => setDrill(null)} />}
    </div>
  );
}
